/**
 * Cart helper shared by the custom `eclipse-*` sections.
 *
 * Those sections POST to /cart/add.js by hand instead of going through the
 * theme's <product-form-component>, so nothing ever told the rest of the theme
 * that the cart had changed: the header bubble and the cart drawer only caught
 * up on the next full page load. Every add now funnels through `add()`, which
 * performs the request and then dispatches the same `cart:update` event the
 * theme's own product form dispatches, with the re-rendered cart sections
 * attached so the drawer morphs in the same round trip.
 *
 * It also keeps quantities inside what is actually in stock. /cart/add.js
 * rejects an oversized add with a 422 and a human-readable message; we surface
 * that message instead of silently resetting the button.
 */
(function () {
  'use strict';

  /** Matches ThemeEvents.cartUpdate in assets/events.js. */
  var CART_UPDATE_EVENT = 'cart:update';

  /** How long the "added" confirmation stays on a button, in ms. */
  var CONFIRMATION_DURATION = 1200;

  /** How long an error toast stays on screen, in ms. */
  var TOAST_DURATION = 4000;

  /** @type {Promise<object|null>|null} Cached /cart.js response, dropped on every cart change. */
  var cartPromise = null;

  /**
   * @returns {{add: string, cart: string}} Localized cart endpoints.
   */
  function routes() {
    var themeRoutes = (window.Theme && window.Theme.routes) || {};

    return {
      // Theme.routes.cart_add_url already carries the .js suffix.
      add: themeRoutes.cart_add_url || '/cart/add.js',
      cart: (themeRoutes.cart_url || '/cart') + '.js',
    };
  }

  /**
   * Reads the cart, reusing the in-flight or last response until it is invalidated.
   * @returns {Promise<object|null>}
   */
  function getCart() {
    if (!cartPromise) {
      cartPromise = fetch(routes().cart, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      })
        .then(function (response) {
          return response.ok ? response.json() : null;
        })
        .catch(function () {
          return null;
        });
    }

    return cartPromise;
  }

  // Any cart change — ours or the theme's — makes the cached copy stale.
  document.addEventListener(CART_UPDATE_EVENT, function () {
    cartPromise = null;
  });

  /**
   * How many units of a variant are already in the cart.
   * @param {string|number} variantId
   * @returns {Promise<number>}
   */
  function quantityInCart(variantId) {
    return getCart().then(function (cart) {
      if (!cart || !cart.items) return 0;

      return cart.items.reduce(function (total, item) {
        return String(item.id) === String(variantId) ? total + item.quantity : total;
      }, 0);
    });
  }

  /**
   * How many more units of a variant may be added before hitting its stock ceiling.
   * @param {string|number} variantId
   * @param {number|null} [stock] - Units in stock, or null when the variant is untracked/oversellable.
   * @returns {Promise<number>} Infinity when there is no ceiling.
   */
  function availableToAdd(variantId, stock) {
    if (stock == null || !isFinite(stock)) return Promise.resolve(Infinity);

    return quantityInCart(variantId).then(function (inCart) {
      return Math.max(0, stock - inCart);
    });
  }

  /**
   * Section ids the theme needs re-rendered, so the drawer and the cart page
   * come back already updated rather than costing another request.
   * @returns {string[]}
   */
  function cartSectionIds() {
    var ids = [];

    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (element) {
      var id = element.dataset.sectionId;
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });

    return ids;
  }

  /**
   * Tells the rest of the theme the cart changed, in the shape assets/events.js uses.
   * @param {object} cart - The updated cart.
   * @param {Record<string, string>} [sections] - Re-rendered cart sections keyed by section id.
   * @param {string|number} [variantId]
   */
  function broadcast(cart, sections, variantId) {
    cartPromise = Promise.resolve(cart);

    document.dispatchEvent(
      new CustomEvent(CART_UPDATE_EVENT, {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'ej-cart',
          data: {
            source: 'ej-cart',
            // Absolute count: cart-icon only adds to the running total for
            // 'product-form-component', otherwise it takes this as the truth.
            itemCount: cart ? cart.item_count : 0,
            variantId: variantId ? String(variantId) : undefined,
            sections: sections || {},
          },
        },
      })
    );
  }

  /**
   * Opens the cart drawer, if the theme has one on the page and it is closed.
   */
  function openDrawer() {
    var drawer = document.querySelector('cart-drawer-component');
    if (!drawer || typeof drawer.open !== 'function') return;

    // The drawer's own auto-open listener may have handled this already.
    if (drawer.querySelector('dialog')?.open) return;

    drawer.open();
  }

  /**
   * Shows a transient message. Used for the stock errors the old code swallowed.
   * @param {string} message
   */
  function toast(message) {
    if (!message) return;

    var host = document.querySelector('.ej-toast-host');
    if (!host) {
      host = document.createElement('div');
      host.className = 'ej-toast-host';
      document.body.appendChild(host);
    }

    var node = document.createElement('div');
    node.className = 'ej-toast';
    node.setAttribute('role', 'status');
    node.textContent = message;
    host.appendChild(node);

    setTimeout(function () {
      node.classList.add('ej-toast--leaving');
      node.addEventListener('transitionend', function () {
        node.remove();
      });
    }, TOAST_DURATION);
  }

  /**
   * Puts a button into its loading state without replacing its label — the old
   * code swapped the text for "..." and left it there on a timer, which is what
   * made every button feel slow.
   * @param {HTMLButtonElement|null|undefined} button
   */
  function startLoading(button) {
    if (!button) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.classList.add('ej-is-loading');
  }

  /**
   * @param {HTMLButtonElement|null|undefined} button
   */
  function stopLoading(button) {
    if (!button) return;

    button.disabled = false;
    button.removeAttribute('aria-busy');
    button.classList.remove('ej-is-loading');
  }

  /**
   * Briefly confirms on the button itself, then restores its original label.
   * @param {HTMLButtonElement|null|undefined} button
   * @param {string} [label]
   */
  function confirm(button, label) {
    if (!button) return;

    if (button.dataset.ejLabel == null) button.dataset.ejLabel = button.textContent || '';
    var original = button.dataset.ejLabel;

    button.textContent = label || '✓';
    button.classList.add('ej-is-added');

    clearTimeout(Number(button.dataset.ejTimer));
    button.dataset.ejTimer = String(
      setTimeout(function () {
        button.textContent = original;
        button.classList.remove('ej-is-added');
      }, CONFIRMATION_DURATION)
    );
  }

  /**
   * Posts one or more lines to the cart and notifies the theme.
   * @param {Array<{id: string|number, quantity?: number}>} lines
   * @returns {Promise<{ok: boolean, message?: string}>}
   */
  function post(lines) {
    var body = {
      items: lines.map(function (line) {
        return { id: Number(line.id), quantity: line.quantity || 1 };
      }),
    };

    var sections = cartSectionIds();
    if (sections.length) {
      body.sections = sections.join(',');
      body.sections_url = window.location.pathname;
    }

    return fetch(routes().add, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    })
      .then(function (response) {
        return response.json().then(function (payload) {
          return { response: response, payload: payload };
        });
      })
      .then(function (result) {
        if (!result.response.ok) {
          // Shopify returns { status, message, description } — description is
          // the sentence worth showing ("You can only add 1 of X to the cart").
          return {
            ok: false,
            message: result.payload.description || result.payload.message || 'No se pudo agregar al carrito.',
          };
        }

        return getCartAfterAdd().then(function (cart) {
          broadcast(cart, result.payload.sections, lines[0] && lines[0].id);
          return { ok: true };
        });
      })
      .catch(function (error) {
        console.error('[ej-cart] add failed', error);
        return { ok: false, message: 'No se pudo agregar al carrito. Revisa tu conexión.' };
      });
  }

  /**
   * The add response describes the added lines, not the whole cart, so the
   * totals have to be read back before the header bubble can be trusted.
   * @returns {Promise<object|null>}
   */
  function getCartAfterAdd() {
    cartPromise = null;
    return getCart();
  }

  /**
   * Adds a single variant to the cart.
   *
   * @param {object} options
   * @param {string|number} options.id - Variant id.
   * @param {number} [options.quantity=1]
   * @param {HTMLButtonElement} [options.button] - Button to drive feedback on.
   * @param {string} [options.addedLabel] - Confirmation label for the button.
   * @param {number|null} [options.stock] - Units in stock, when the variant is tracked.
   * @param {string} [options.redirectTo] - Navigate here on success instead of opening the drawer.
   * @returns {Promise<boolean>} Whether the item was added.
   */
  function add(options) {
    var button = options.button;
    var quantity = Math.max(1, parseInt(String(options.quantity), 10) || 1);

    if (!options.id) return Promise.resolve(false);

    startLoading(button);

    return availableToAdd(options.id, options.stock).then(function (room) {
      if (room <= 0) {
        stopLoading(button);
        toast('Ya tienes todas las unidades disponibles de este producto en tu carrito.');
        return false;
      }

      if (quantity > room) {
        stopLoading(button);
        toast('Solo quedan ' + room + (room === 1 ? ' unidad disponible.' : ' unidades disponibles.'));
        return false;
      }

      return post([{ id: options.id, quantity: quantity }]).then(function (result) {
        stopLoading(button);

        if (!result.ok) {
          toast(result.message || '');
          return false;
        }

        if (options.redirectTo) {
          window.location.href = options.redirectTo;
          return true;
        }

        confirm(button, options.addedLabel);
        openDrawer();
        return true;
      });
    });
  }

  /**
   * Adds several variants in a single request, so a bundle costs one round trip
   * instead of one per item. Shopify rejects the whole batch if any line fails.
   *
   * @param {Array<{id: string|number, quantity?: number}>} lines
   * @param {object} [options]
   * @param {HTMLButtonElement} [options.button]
   * @param {string} [options.addedLabel]
   * @param {string} [options.redirectTo]
   * @returns {Promise<boolean>}
   */
  function addMany(lines, options) {
    options = options || {};
    var usable = (lines || []).filter(function (line) {
      return line && line.id;
    });

    if (!usable.length) return Promise.resolve(false);

    startLoading(options.button);

    return post(usable).then(function (result) {
      stopLoading(options.button);

      if (!result.ok) {
        toast(result.message || '');
        return false;
      }

      if (options.redirectTo) {
        window.location.href = options.redirectTo;
        return true;
      }

      confirm(options.button, options.addedLabel);
      openDrawer();
      return true;
    });
  }

  /**
   * Convenience wrapper for the quick-add buttons in the card grids, which
   * carry their variant and stock ceiling as data attributes.
   *
   * @param {HTMLButtonElement} button - Needs data-id; data-stock is optional
   *   and empty when the variant is untracked or oversellable.
   * @param {object} [options] - Passed through to `add()`.
   * @returns {Promise<boolean>}
   */
  function addFromButton(button, options) {
    options = options || {};

    var rawStock = button.getAttribute('data-stock');
    var stock = rawStock === null || rawStock === '' ? null : parseInt(rawStock, 10);

    return add({
      id: button.getAttribute('data-id'),
      quantity: parseInt(button.getAttribute('data-quantity'), 10) || 1,
      button: button,
      stock: isNaN(/** @type {number} */ (stock)) ? null : stock,
      addedLabel: options.addedLabel,
      redirectTo: options.redirectTo,
    });
  }

  window.EJCart = {
    add: add,
    addFromButton: addFromButton,
    addMany: addMany,
    availableToAdd: availableToAdd,
    quantityInCart: quantityInCart,
    getCart: getCart,
    toast: toast,
  };
})();
